import { match } from "ts-pattern";
import { cn } from "@/lib/utils";
import { AgentMessage } from "@/models/agent/message";
import FileDownloadButtons from "./FileDownloadButtons";
import MarkdownRenderer from "./MarkdownRenderer";
import ToolCallMessage from "./ToolCallMessage";
import ToolResponseMessage from "./ToolResponseMessage";

interface MessageItemUIProps {
  message: AgentMessage;
  className?: string;
  onToolClick?: (message: AgentMessage) => void;
}

const UserMessage: React.FC<{
  message: AgentMessage;
  className?: string;
}> = ({ message, className }) => {
  return (
    <div className={cn("group relative mb-4 flex justify-end", className)}>
      <div className="flex flex-col mr-4 max-w-[65%] items-end">
        <div
          className={cn(
            "relative group/message py-3 px-4 rounded-[18px] bg-[#d8e4f5]",
          )}
        >
          <div className="break-words whitespace-pre-wrap markdown text-[#474747] text-[16px] leading-[1.5]">
            {message.textContent}
          </div>
        </div>
      </div>
    </div>
  );
};

const AssistantMessage: React.FC<{
  message: AgentMessage;
  className?: string;
}> = ({ message, className }) => {
  const showDownloadButtons = ["assistant_response_end", "assistant"].includes(message.role)
    && !message.metadata?.thought;

  return (
    <div className={cn("group relative mb-4 flex justify-start break-all", className)}>
      <div className="flex flex-col items-start">
        <div
          className={cn("relative group/message py-3 rounded-2xl")}
        >
          <div className="break-words">
            <div className="prose prose-sm max-w-none">
              {message.metadata?.thought
                ? <div className="italic text-xs text-gray-400 leading-[1.8]">{message.textContent}</div>
                : (
                  <MarkdownRenderer
                    content={message.isCompaction ? "途中経過の要約を作成しました。" : message.textContent}
                  />
                )}
            </div>
          </div>
        </div>

        {showDownloadButtons && <FileDownloadButtons content={message.textContent} />}
      </div>
    </div>
  );
};

const MessageItemUI: React.FC<MessageItemUIProps> = ({
  message,
  className,
  onToolClick,
}) => {
  return match(message.role)
    .returnType<JSX.Element | null>()
    .with("tool_call", () => <ToolCallMessage message={message} className={className} onToolClick={onToolClick} />)
    .with("tool_response", () => <ToolResponseMessage message={message} className={className} />)
    .with("user", () => <UserMessage message={message} className={className} />)
    .with("assistant", "assistant_response_end", () => (
      <AssistantMessage
        message={message}
        className={className}
      />
    ))
    .otherwise(() => {
      console.warn("Unknown message role:", message.role);
      return null;
    });
};

export default MessageItemUI;
